import { FolderPathIntent } from '../v2/components/Input/folderPath'
import { UserListItemDto as IUserListItemDto } from './userConfig'

import { BaseTierDto, BaseUserTierDto } from './finance/financeConfig'
import { ReplicationDto } from './emailConfig'
import { Billable } from './registration/timeConfig'
import { DimensionPivotValues } from '../v2/components/Form/Accounting/DimensionFields'
import { FileModuleDto } from '../hooks/module/useFileModule'
import { AttributeEntityDto } from '../hooks/useAttributes'
import { FileSurchargeDto } from '../components/Form/File/Surcharge/FileSurchargeList'
import { FileRateType, FileScaffoldAuthorizationDto } from '../context/GlobalContext'

export interface IReferenceDto {
    id: string
    reference: string
    name: string
    ownerId: string
}

export interface IOverviewItemDto {
    id: string
    owner: IUserListItemDto
    executiveIds?: string[]
    reference: string
    name: string
    normalizedName: string
    documentRootId: string
    sharedDocumentRootURL?: string
    currency: string
    fileRateType: FileRateType
    rate: string
    // Per-file override of the kilometre rate (HAM-3627); empty means the organisation-wide
    // TRAVEL_RATE setting (or the € 0,23 default) applies.
    travelRate?: string
    language: string
    status: FileStatus
    workflows: FileWorkflowInstanceDto[] // Active workflow instances, with sequence numbers.

    pinned?: boolean
    lastAccess?: string
}

export type SummaryDto = IOverviewItemDto & {
    description: string
    notification?: string
    invoiceEmail?: string
}

/**
 * One workflow instance attached to a file. `id` is undefined for a new (unsaved) instance; the
 * server assigns the sequence number. `clientKey` is a client-only stable key for unsaved
 * instances and is stripped before submit.
 */
export type FileWorkflowInstanceDto = {
    id?: string
    workflowId: string
    sequenceNumber?: number
    clientKey?: string
}

export type FileMutationDto<T extends BaseUserTierDto = BaseUserTierDto> = RateDto<T> &
    AttributeEntityDto & {
        created?: string
        organisationProfileKey?: string
        status: FileStatus
        reference: string
        name: string
        description?: string
        notification?: string
        ownerId: string
        kyc?: boolean
        timeTrackingPopup?: boolean
        crmRelationId?: string
        executiveIds?: string[]
        documentRootId?: string
        documentRootIntent?: FolderPathIntent // used in frontend only; applied on submit
        sharedDocumentRootURL?: string
        language: string
        vatType: string
        travelRate?: string
        paymentCurrency?: string
        dateOfIncident?: string
        archiveLocation?: string
        invoiceName?: string
        invoiceEmail?: string
        unit?: string
        workflows?: FileWorkflowInstanceDto[]
        accounting?: DimensionPivotValues
        authorization?: FileScaffoldAuthorizationDto
        modules?: FileModuleDto[]
        surcharges?: FileSurchargeDto[]
    }

export type RateDto<T extends BaseTierDto> = {
    billable?: Billable
    fileRateType: FileRateType
    currency: string
    rate?: string
    tiers?: T[]
}

export type ScaffoldDto = Pick<
    FileMutationDto,
    | 'accounting'
    | 'authorization'
    | 'billable'
    | 'currency'
    | 'fileRateType'
    | 'language'
    | 'paymentCurrency'
    | 'rate'
    | 'reference'
    | 'status'
    | 'surcharges'
    | 'timeTrackingPopup'
    | 'unit'
    | 'vatType'
    | 'workflows'
>

export interface IFileDto extends IOverviewItemDto, Omit<AttributeEntityDto, 'currency'> {
    organisationProfileKey?: string
    description: string
    notification?: string
    archiveLocation: string
    invoiceName: string
    invoiceEmail: string
    vatType: string
    dateOfIncident: string
    unit?: string
    accounting?: DimensionPivotValues
    modules?: FileModuleDto[]
    paymentCurrency?: string
    kyc: boolean
    crmRelationId?: string
    mailFolder?: ReplicationDto // Contains the user-specific replication folders for this file.
    replication?: ReplicationDto // Contains the shared mailbox-specific replication folders for this file.
}

export enum FileStatus {
    Open = 'OPEN',
    Archived = 'ARCHIVED',
}

export enum FileAction {
    GetMutation = 'GET_MUTATION_FILE',
    GetList = 'GET_FILE_LIST',
    GetEmails = 'GET_FILE_EMAILS',
    Get = 'GET_FILE',
    Add = 'ADD_FILE',
    Update = 'UPDATE_FILE',
    Delete = 'DELETE_FILE',
    ResetCurrent = 'RESET_CURRENT_FILE',
    ResetList = 'RESET_FILE_LIST',
    ResetEmails = 'RESET_FILE_EMAILS',
}
export type BudgetMonitoringDto = {
    fileId: string

    billedHours: number
    billedHoursPrice: string

    outstandingHours: number
    outstandingHoursPrice: string

    billedTravelsPrice: string
    outstandingTravelsPrice: string

    billedDisbursementsPrice: string
    outstandingDisbursementsPrice: string
}
